import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Avatar,
  List,
  ListItem
} from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth, getToken } from '../components/auth/AuthContext';
import { Hotel } from '../types/hotel';
import { APIHOST } from '../common';

export default function HotelAdminPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchAdminHotels = async () => {
      if (!currentUser?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        const token = getToken();
        const response = await fetch(
          `${APIHOST}/api/hotel-admin/hotels`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch hotels: ${response.statusText}`);
        }

        const data = await response.json();
        setHotels(data.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hotels');
        setHotels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminHotels();
  }, [currentUser?.id]);

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container  sx={{ py: 4 }}>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          My Hotels
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Hotels where you are an administrator
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {hotels.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <HotelIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            You are not an administrator for any hotels yet.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ bgcolor: 'background.paper' }}>
          {hotels.map((hotel, index) => (
            <Box
              key={hotel.documentId}
              onClick={() => navigate(`/hotel-admin/hotels/${hotel.documentId}`)}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                borderBottom: index < hotels.length - 1 ? '1px solid #e0e0e0' : 'none',
                '&:hover': {
                  bgcolor: 'action.hover',
                  cursor: 'pointer'
                }
              }}
            >
              <Avatar sx={{ bgcolor: 'primary.main', flexShrink: 0 }}>
                <HotelIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {hotel.name}
                  </Typography>
                  {hotel.longName && hotel.longName !== hotel.name && (
                    <Typography variant="body2" color="text.secondary">
                      ({hotel.longName})
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={2}>
                  {(hotel.addressCity || hotel.addressState) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {[hotel.addressCity, hotel.addressState]
                          .filter(Boolean)
                          .join(', ')}
                      </Typography>
                    </Box>
                  )}
                  {hotel.admins && hotel.admins.length > 0 && (
                    <Chip
                      icon={<PersonIcon />}
                      label={`${hotel.admins.length} admin${
                        hotel.admins.length === 1 ? '' : 's'
                      }`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
}
